/**
 * Felklass för ett misslyckat eller ofullständigt svar — ELLER ett degraderat
 * lyckat svar (`fallback_model`: svaret kom från reservmodellen efter failover
 * i selfbuilt-generate.server.ts; ingen felram, men samma Plausible-vy som vertex_429/
 * vertex_timeout så "hur ofta faller vi över" delar nämnare med felen).
 * Servern (chat.ts) klassar och skickar klassen som datapart `answerError`
 * före felramen; klienten (chat-page.tsx) skickar den som Plausible-prop
 * `kind`. Sluten mängd: bara dessa värden får lämna klienten (privacy-regeln
 * i plausible.client.ts). Klientsäker modul — inga imports.
 */
/**
 * OBS namnen `vertex_*`: de betyder "fel från MODELLEVERANTÖREN", inte "fel
 * från Vertex". Motorn kan sedan 2026-09-18 svara via Vertex (Gemini) ELLER
 * OpenAI, och båda klassas i samma kinds. Namnen behålls med flit:
 * mängden är ett klientkontrakt (privacy-regeln i plausible.client.ts),
 * Plausible-historiken sträcker sig bakåt på dem, och driftlarmet
 * (scripts/answer-error-alert.mjs) filtrerar på dem. Ett byte hade kostat
 * klient, dashboard och larm utan att ge något mätvärde tillbaka.
 */
export const ANSWER_ERROR_KINDS = [
	'vertex_429',
	'vertex_5xx',
	'vertex_timeout',
	'vertex_auth',
	'fallback_model',
	'retrieval',
	'other',
	'truncated',
	'max_tokens',
	'blocked',
] as const
export type AnswerErrorKind = (typeof ANSWER_ERROR_KINDS)[number]
export const ANSWER_ERROR_DATA_TYPE = 'answerError'
export type AnswerErrorPayload = {
	kind: AnswerErrorKind
	/** 'docsbot' är bara historiskt (DocsBot-vägen borttagen i IM-61): värdet
	 *  finns i Plausible-historiken för answer_error och står kvar i typen. */
	engine: 'selfbuilt' | 'docsbot'
}

/**
 * Klassar ett kastat fel ur egen motor på felmeddelandets form, exakt som
 * selfbuilt-generate/-retrieve.server.ts bygger det. Konservativt: allt okänt → 'other'.
 * Ändras felsträngarna där måste tabelltestet i answer-error.test.ts
 * uppdateras — därför radhänvisningarna nedan.
 */
export function classifyEngineError(error: unknown): AnswerErrorKind {
	const message = error instanceof Error ? error.message : String(error)
	// selfbuilt-generate.server.ts, generateGemini:s statuskast — `Gemini/vertex <status>: <body>`
	// efter alla retries (och efter failover: reservmodellens fel bär samma form).
	// Referenserna här pekar på SYMBOL, inte radnummer: de senare har ruttnat två
	// gånger på en och samma PR när generate.mjs växte ovanför dem.
	const statusMatch = /^(?:Gemini\/vertex|OpenAI) (\d{3})\b/.exec(message)
	if (statusMatch) {
		const status = Number(statusMatch[1])
		if (status === 429) return 'vertex_429'
		if (status === 401 || status === 403) return 'vertex_auth'
		if (status >= 500 && status <= 599) return 'vertex_5xx'
		return 'other'
	}
	// selfbuilt-generate.server.ts, streamPost:s idle-timer — 45 s (`req.destroy`); streamPost:s
	// first-token-timer — 5 s för en modell som kan failovra, 25 s för den sista
	// i kedjan (`… (första token)`, res/req.destroy). Prefixet är transportens
	// etikett (`label`). Siffran i meddelandet skrivs ur värdet, så matchningen
	// sker på prefixet och tål att deadlinen ändras.
	if (/^(?:Gemini\/vertex|OpenAI): inget svar/.test(message))
		return 'vertex_timeout'
	// selfbuilt-generate.server.ts, transporternas `error`-gren — fel MITT i
	// strömmen (200 + `data:{error}`). Koden ligger i den serialiserade kroppen;
	// utan detta blir en DSQ-429 efter första token 'other' (retryn i
	// postWithRetry gäller bara före strömstart). OpenAI-strömmen bär en feltyp
	// i stället för numerisk kod; transporten mappar typen till `code`
	// (OPENAI_ERROR_STATUS) innan meddelandet byggs, så samma gren klassar båda.
	const streamErr = /^(?:Gemini\/vertex|OpenAI) stream error: (.+)$/s.exec(
		message,
	)
	if (streamErr) {
		const body = streamErr[1] ?? ''
		const code = /"code"\s*:\s*(\d{3})/.exec(body)
		const status = code ? Number(code[1]) : 0
		if (status === 429 || /RESOURCE_EXHAUSTED/.test(body)) return 'vertex_429'
		if (status === 401 || status === 403) return 'vertex_auth'
		if (status >= 500 && status <= 599) return 'vertex_5xx'
		return 'other'
	}
	// selfbuilt-retrieve.server.ts, raceTimeout — token-utbytet kan också bara ta slut på
	// tid; det är latens, inte credentials.
	if (/^vertex token-exchange: inget svar/.test(message))
		return 'vertex_timeout'
	// selfbuilt-retrieve.server.ts, vertexAccessToken — `vertex token-exchange <status>` kastas för VARJE non-ok
	// status. Statusen måste läsas innan det blir auth: 5xx/429 från oauth2-
	// endpointen är tillfälligt, inte credentials.
	const tokenStatus = /^vertex token-exchange (\d{3})\b/.exec(message)
	if (tokenStatus) {
		const status = Number(tokenStatus[1])
		if (status === 429) return 'vertex_429'
		if (status >= 500 && status <= 599) return 'vertex_5xx'
		return 'vertex_auth'
	}
	// selfbuilt-retrieve.server.ts, vertexAccessToken + selfbuilt-generate.server.ts,
	// generateGemini:s saJson-vakt — token-utbyte/servicekonto
	if (
		/^(vertex token-exchange|GCP_SA_JSON|Gemini via Vertex kräver|OpenAI kräver OPENAI_API_KEY)/.test(
			message,
		)
	)
		return 'vertex_auth'
	// selfbuilt-retrieve.server.ts, raceTimeout/multiSearch — Typesense-timeout/HTTP-fel/abort
	if (/^Typesense\b/.test(message)) return 'retrieval'
	return 'other'
}
